<template>
  <drop-list
    :items="documentNotes"
    class="notepad-wrapper row no-gutters"
    @insert="updateDrag"
    @reorder="updateDrag"
    mode="cut"
    accepts-type="item"
    :column="true"
    :row="false"
  >
    <template v-slot:item="{ item, index }">
      <drag
        :key="item.id + '-drag'"
        :data="{
          id: item.id,
          isNoteCard: item.isNoteCard,
          pageId: item.pageId,
          order: item.order,
          fromPageId: pageId
        }"
        type="item"
        class="col dl col-12"
        :handle="`.a${item.id}`"
        :drag-image-opacity="1"
        :scrolling-edge-size="0"
        @cut="log"
      >
        <template v-slot:drag-image>
          <drag-image :order="item.order" :isSelectedPage="isSelectedPage"></drag-image>
        </template>
        <content-block
          class="note-block"
          :ref="item.id"
          :key="item.id"
          :note="item"
          :nested="nested"
          :nestedLevel="nestedLevel"
          :pageId="parentPageId"
          :parentNoteId="item.id"
          :parentPageId="parentPageId"
          :outline="outline"
          :showParent="showParent"
          :parentPageNoteLength="pageNoteLength"
          :slowLoad="slowLoad"
          :delayLoad="delayLoad"
          :docMounted="mounted"
          :dragSelecting="selectedPageId == pageId && dragSelecting"
          :isSelectedPage="isSelectedPage"
          :allowQuoteAttr="allowQuoteAttr"
          :index="index"
          :dashboardView="dashboardView"
          :gridView="gridView"
          :participants="participants"
          @focused="$emit('docFocused')"
          @blurred="$emit('docBlurred')"
          @startDragSelect="startDragSelect($event, item)"
          @stopDragSelecting="updateDragSelecting(false)"
          @selectNote="selectNote"
          @selectMouseEnter="toggleSelection"
          @clearDragSelection="clearSelection"
          @indentCard="handleIndentCard({ noteId: item.id, event: $event, order: item.order })"
          @deIndentCard="handleDeIndentCard({ noteId: item.id, event: $event, order: item.order })"
          @moveOrder="moveOrder({ noteId: item.id, event: $event, order: item.order })"
          @addNewLine="addNewLine"
          @updateSelectedNotes="$emit('updateSelectedNotes', $event)"
          @updateSelectedNotesPage="$emit('updateSelectedNotesPage', $event)"
          @traverseBlocks="traverseBlocks"
          @moveToLine="moveToLine"
          @pasteBlocks="pasteNoteBlocks(item.order, $event)"
        ></content-block>
      </drag>
    </template>
    <template v-slot:feedback="{ data }">
      <div class="pl-5 reordering-feedback" :key="data.id + '-feedback'">
        <div class="" />
      </div>
    </template>
    <template v-slot:reordering-feedback="{ item }">
      <div class="pl-5 reordering-feedback" :key="item.id + '-reorder-feedback'">
        <div class="" />
      </div>
    </template>
  </drop-list>
</template>

<script>
import ContentBlock from './ContentBlock';
import DragImage from './DragImage.vue';
import { Drag, DropList, DragAwareMixin } from 'vue-easy-dnd';
import { computed, inject } from '@vue/composition-api';
import { pagesModule } from '@/store/database/pagesModule';
import useMultiSelect from '@/views/Context/use/useMultiSelect';
import useClipboard from '@/views/Context/use/useClipboard';
import {
  addNote,
  updatePage,
  moveNotes,
  duplicateNotes,
  revertFromCard
} from './use/useDocActions';

export default {
  name: 'ContextDoc',
  mixins: [DragAwareMixin],
  components: { ContentBlock, Drag, DropList, DragImage },
  props: {
    pageId: {},
    noteId: {},
    delayLoad: {},
    nested: {
      type: Boolean,
      default: false
    },
    isPageContent: {
      type: Boolean,
      default: false
    },
    nestedLevel: {
      type: Number,
      default: 0
    },
    outline: {
      type: Boolean,
      default: false
    },
    dashboardView: {
      type: Boolean,
      default: false
    },
    gridView: {
      type: Boolean,
      default: false
    },
    pinned: {
      type: Boolean,
      default: false
    },
    selectedNote: {
      type: Object,
      default() {
        return {};
      }
    },
    allowQuoteAttr: {
      type: Boolean,
      default: false
    },
    parentType: {
      type: String,
      default: 'card'
    },
    docNotes: {
      type: Array,
      default() {
        return [];
      }
    },
    nestedNotes: {
      type: Array,
      default() {
        return [];
      }
    },
    participants: {
      type: Object,
      default() {
        return {};
      }
    },
    showParent: {
      type: Boolean,
      default: true
    },
    parentPageId: {
      type: String,
      default: undefined
    },
    parentNoteId: {
      type: String,
      default: undefined
    },
    pageNoteLength: {
      type: Number,
      default: 1
    }
  },
  setup(props) {
    // const { pages } = usePages();
    // const page = computed(() => {
    //   return pages[props.pageId].value;
    // });
    const documentNotes = computed(() => {
      // console.log(page);
      // console.log('compute doc notes from contextdoc');
      const pModule = pagesModule.doc(props.pageId);
      const page = pModule.data;
      if (page) {
        if (!props.nested && props.noteId) {
          return [{ id: props.parentNoteId, pageId: props.pageId, type: 'card' }];
        } else if (!props.nested) {
          // console.log(pageId);
          return [{ id: props.parentNoteId, pageId: props.pageId, type: 'card' }];
        } else {
          return Object.values(page.pageNotes)
            .filter(n => !n.toDelete && n !== undefined && n.id)
            .sort((a, b) => (a.order > b.order ? 1 : -1));
        }
        // .map(n => ({ pageId: n.pageId, type: n.type, text: n.content.html }));
      } else {
        return [];
      }
    });
    const findPage = pageId => pagesModule.doc(pageId).data;
    const {
      updateDragSelecting,
      updateSelectedRange,
      clearSelection,
      dragSelecting,
      selectedPageId,
      getSelectedNoteIds,
      checkIsSelected,
      selectedSpace,
      startOrder,
      endOrder
    } = useMultiSelect();

    const selectedRange = computed(() => {
      return [startOrder.value, endOrder.value];
    });
    const { clipboard } = useClipboard();

    const updatePageType = ({ pageId, type }) => {
      updatePage({ pageId, update: { type } });
    };

    const isSelectedPage = computed(() => {
      // console.log('check selectedPage', selectedPageId);
      return selectedPageId.value == props.pageId;
    });

    const tabId = inject('tabId');

    return {
      documentNotes,
      findPage,
      updateDragSelecting,
      clearSelection,
      updateSelectedRange,
      dragSelecting,
      selectedPageId,
      isSelectedPage,
      getSelectedNoteIds,
      selectedSpace,
      checkIsSelected,
      selectedRange,
      clipboard,
      addNote,
      moveNotes,
      duplicateNotes,
      updatePageType,
      tabId
    };
  },
  data() {
    return {
      slowLoad: true,
      // dragSelected: false,
      renderIndex: 0,
      mounted: false,
      docFocused: false
    };
  },
  mounted() {
    this.$emit('mounted');
    this.mounted = true;
    //  turn off slowload after intial load
    setTimeout(() => (this.slowLoad = false), 3000);
    // if (!this.documentNotes.length) {
    //   this.mounted = true;
    //   this.$emit('rendered');
    // }
  },
  destroyed() {
    // document.removeEventListener('keypress', this.copyTrigger);
    document.removeEventListener('keyup', this.selectBlockHotKeys);
  },
  watch: {
    docFocused: function(n, o) {
      console.log(n);
      if (n) {
        this.$emit('focus');
      } else {
        // setTimeout(() => {
        //   if (!this.docFocused) {
        //     //  emit blur if after a beat we are still not focused
        //     // this.$emit('blur');
        //   }
        // }, 100);
      }
    },
    selectedPageId: function(newValue, oldValue) {
      console.log(newValue);
      //  listen handler for tab and shift tab
      if (newValue == this.pageId && oldValue != this.pageId && this.nested) {
        if (this.tabId == this.selectedSpace) {
          console.log('add select block listener', this);
          document.addEventListener('keyup', this.selectBlockHotKeys);
        }
      } else if (oldValue == this.pageId) {
        console.log('remove select block listener');
        document.removeEventListener('keyup', this.selectBlockHotKeys);
      }
    }
  },
  methods: {
    selectBlockHotKeys(event) {
      console.log(event);
      const key = event.which || event.keyCode;
      // if (key == 67 && event.ctrlKey) {
      //   this.copyBlocks();
      // }
      // if (key == 46 || key == 8) {
      //   //  handle delete
      //   this.deleteSelectedBlocks();
      // }
      if (key == 9) {
        if (event.shiftKey) {
          //  shift tab the selection back
          this.handleDeIndentCard({ event, noteId: false });
        } else {
          //  tab
          this.handleIndentCard({ event, noteId: false });
        }
      }
    },
    updateDrag(event, data) {
      console.log(event);
      console.log(this.dragData);
      let noteIds = [];
      const dragItem = this.dragData;
      //  check if we are dropping a note card to embedd
      const moveToPosition = event.to == undefined ? event.index : event.to;
      if (dragItem.isNoteCard) {
        //  create new note with note card embedd
        //  add a new note with the page as the embedded page
        this.addNote({
          pageId: this.pageId,
          order: moveToPosition,
          embeddedPageId: dragItem.pageId
        });

        //  remove note state from page
        revertFromCard({ pageId: dragItem.pageId });
      } else {
        const selectedNoteIds = this.getSelectedNoteIds();
        if (selectedNoteIds.includes(dragItem.id)) {
          //  multidrag
          noteIds = selectedNoteIds;
        } else {
          noteIds.push(dragItem.id);
        }

        // const noteIds = [dragContext.element.id];
        console.log(this.page);
        const moveToPageId = this.pageId;
        const moveFromPageId = dragItem.fromPageId;
        const moveToPosition = event.to == undefined ? event.index : event.to;
        if (moveToPosition < 0) return;
        console.log('update drag', noteIds, moveToPageId, moveFromPageId, moveToPosition);
        this.moveNotes({ noteIds, moveToPageId, moveFromPageId, moveToPosition });
        this.clearSelection();
      }
    },
    moveOrder({ noteId, event, order, forceMove }) {
      console.log(noteId, event);
      let newIndex;
      let noteIds = [noteId];
      let rangeUpdate = false;
      if (this.checkIsSelected({ order: order, pageId: this.pageId })) {
        if (!forceMove) {
          return; //if selected and not forceMove, then don't move. This prevents duplicate moves being called if multi select and text focused
        }
        //  multi-indent
        console.log('move multi select');
        noteIds = this.getSelectedNoteIds();
        console.log(this.selectedRange);
        const range = this.selectedRange;
        //  figure out where we are going.
        //  Probably need to get the selected range, and add one or minus one from the biggest/smallest
        if (event.to) {
          newIndex = event.to;
        } else {
          if (event.amount > 0) {
            newIndex = Math.max(range[0], range[1]) + 1;
          } else {
            newIndex = Math.min(range[0], range[1]) - 1;
          }
          rangeUpdate = { startOrder: range[0] + event.amount, endOrder: range[1] + event.amount };
          console.log(rangeUpdate);
        }
      } else {
        newIndex = event.to || order + event.amount;
      }
      console.log(newIndex);
      // if (this.selectedNoteBlocks.includes(itemId)) {
      //   //  multidrag
      //   noteIds = this.selectedNoteBlocks;
      // } else {
      //   noteIds.push(dragItem.id);
      // }

      //  If we are at the top of  card, then pull it out
      if (newIndex < 0) {
        this.handleDeIndentCard({ noteId, order, orderOffset: 0 });
        return;
      } else if (newIndex >= this.documentNotes.length) {
        //  if we are at the end of the card, pull it out
        this.handleDeIndentCard({ noteId, order });
        return;
      } else {
        //  otherwise, check if the card in the position we are going to is open or not
        //  if open, put it inside that card
        const noteToSwap = this.documentNotes[newIndex];
        const noteRef = this.$refs[noteToSwap.id];
        console.log(noteToSwap);
        if (noteRef.$refs.embeddedCard.show) {
          console.log(noteRef);
          const noteToSwapChildCount = noteRef.$refs.embeddedCard.pageNoteLength;
          const newOrder = event.amount > 0 ? 0 : noteToSwapChildCount;
          console.log(noteToSwapChildCount);
          //  move this note inside here
          this.moveNotes({
            noteIds,
            moveToPageId: noteToSwap.pageId,
            moveFromPageId: this.pageId,
            moveToPosition: newOrder
          }).then(() => {
            this.$nextTick(() => {
              this.clearSelection();
              this.$nextTick(() => {
                noteRef.focusNestedNote({ index: newOrder });
              });
            });
          });
        } else {
          this.moveNotes({
            noteIds,
            moveToPageId: this.pageId,
            moveFromPageId: this.pageId,
            moveToPosition: newIndex
          }).then(() => {
            this.$nextTick(() => {
              this.focusNoteBlock({ noteId });
              if (rangeUpdate) {
                this.updateSelectedRange(rangeUpdate);
              }
            });
          });
        }
      }
    },
    handleIndentCard({ event, noteId, order }) {
      console.log('indent');
      let noteIds = [];
      const isSelected = this.checkIsSelected({ order: order, pageId: this.pageId });
      if (!noteId || isSelected) {
        //  multi-indent
        noteIds = this.getSelectedNoteIds();
      } else {
        noteIds.push(noteId);
      }
      //  get lowest note index

      const noteIndexes = noteIds.map(n => this.documentNotes.findIndex(d => d.id == n));
      const minIndex = Math.min(...noteIndexes);
      // const selectedNoteIndex = this.documentNotes.findIndex(d =>
      //   noteId ? d.id == noteId : d.id == noteIds[0]
      // );
      // const thisNotePageId = this.documentNotes[selectedNoteIndex].pageId;

      if (minIndex === 0) {
        //  no previous note to nest inside, just return
        return;
      }
      //  get previous sibling
      const prevNote = this.documentNotes[minIndex - 1];
      //  get the prevNote page
      const prevNotePageId = prevNote.pageId;
      console.log(this.documentNotes, noteIds, minIndex, prevNote, prevNotePageId);
      if (prevNotePageId) {
        const prevNotePage = this.findPage(prevNotePageId);
        console.log(prevNotePage);
        const moveToPosition = Object.values(prevNotePage.pageNotes).filter(
          n => !n.toDelete && n !== undefined && n.id
        ).length;
        //  Open the previous card
        this.$refs[prevNote.id].setShow(true);

        //  change icon to bullet if empty and only one
        console.log(noteIds.length, event.empty);
        if (noteIds.length == 1 && event.empty) {
          const note = this.documentNotes[minIndex];
          this.updatePageType({ pageId: note.pageId, type: 0 });
        }
        this.moveNotes({
          noteIds: noteIds,
          moveToPageId: prevNotePageId,
          moveFromPageId: this.pageId,
          moveToPosition
        }).then(() => {
          this.$nextTick(() => {
            this.clearSelection();
            this.$refs[prevNote.id].setShow(true);
            this.$nextTick(() => {
              if (isSelected) {
                //  update selection
                const rangeUpdate = {
                  startOrder: moveToPosition + event.amount,
                  endOrder: moveToPosition + noteIds.length - 1,
                  selectedPageId: prevNotePageId
                };
                this.updateSelectedRange(rangeUpdate);
              } else {
                this.$refs[prevNote.id].focusNestedNote({ index: moveToPosition });
              }
            });
          });
        });
      }
    },
    handleDeIndentCard({ event, noteId, order, orderOffset = 1 }) {
      console.log('deindent', noteId);
      let noteIds = [];
      let selectedNoteId;
      if (!noteId || this.checkIsSelected({ order: order, pageId: this.pageId })) {
        //  multi-indent
        console.log('multi select!');
        noteIds = this.getSelectedNoteIds();
        selectedNoteId = noteIds[0];
      } else {
        noteIds.push(noteId);
        selectedNoteId = noteId;
      }
      if (!this.nested) {
        //  fully deindented, can't deindent more
        return;
      }
      //  get note index
      // const noteIndexes = noteIds.map(n => this.documentNotes.findIndex(d => d.id == n));
      // const minIndex = Math.min(...noteIndexes);
      // const selectedNoteIndex = this.documentNotes.findIndex(d => d.id == selectedNoteId);
      const targetPageId = this.$parent.parentPageId;
      console.log(targetPageId, this.$parent);
      const targetPrevSiblingNoteId = this.$parent.parentNoteId;
      const targetPage = this.findPage(targetPageId);
      const targetPrevSiblingNote = targetPage.pageNotes[targetPrevSiblingNoteId];
      const targetOrder = targetPrevSiblingNote.order + orderOffset;
      // const siblingsToMove = this.documentNotes.slice(noteIndex + 1);
      //  Move the card

      // const startOrder = Object.values(cardPage.pageNotes).filter(n => !n.toDelete).length;
      this.moveNotes({
        noteIds: noteIds,
        moveToPageId: targetPageId,
        moveFromPageId: this.pageId,
        moveToPosition: targetOrder
      }).then(() => {
        this.$nextTick(() => {
          console.log(this.$parent.$parent.$parent.$parent.$parent.$parent.$refs, noteIds[0]);
          this.$parent.$parent.$parent.$parent.$parent.$parent.$refs[noteIds[0]].focus();
          this.clearSelection();
        });
      });
    },
    log(x) {
      console.log(x);
    },
    pasteNoteBlocks(order, replaceNote) {
      console.log('paste');
      console.log(this.clipboard);
      const { type } = this.clipboard;
      if (type == 'block') {
        const { contents, parent } = this.clipboard.blockData;
        this.duplicateNotes({
          noteIds: contents,
          startingPosition: order,
          fromPageId: parent,
          toPageId: this.pageId,
          replaceNote
        });
      } else if (type == 'clip') {
        const { resourceId, clipId } = this.clipboard.clipData;
        const resourceData = {
          pageId: this.pageId,
          html: false,
          order,
          cardId: false,
          widgetInfo: {
            widgetType: 'resource',
            widgetData: {
              resourceId: resourceId,
              resourceVersion: 1,
              resourceNoteIds: clipId ? [clipId] : []
            }
          }
        };
        this.addNote(resourceData);
      }
    },
    startDragSelect(e, note) {
      // if (this.dragInProgress) {
      //   console.log('drag in progress');
      //   return;
      // }
      this.updateDragSelecting(true, e);
      console.log(this.dragSelecting);
      // this.dragSelected = true;
      this.selectNote(note);
      // this.selectedNoteBlocks = [noteId];
      // this.$emit('updateSelectedNotes', { start: id, end: id });
      // this.$emit('updateSelectedNotesPage', this.pageId);
      // this.$refs.dragSelect.onMouseDown(e);
    },
    selectNote(note) {
      console.log('selectNote');
      this.updateSelectedRange({
        start: note.id,
        startOrder: note.order,
        pageRef: this,
        end: note.id,
        endOrder: note.order,
        pageId: this.pageId,
        space: this.tabId
      });
    },
    toggleSelection({ note }) {
      // console.log('toggle selection', this.dragInProgress);
      // if (this.dragInProgress) {
      //   this.$refs.dragSelect.onMouseUp();
      //   this.updateDragSelecting(false);
      //   this.clearSelection();
      // } else {
      this.updateSelectedRange({ end: note.id, endOrder: note.order });
      // }
    },
    async addNewLine({ order, delta, html = '', type, pageType, pageId = this.pageId }) {
      console.log(delta);
      if (this.pageId) {
        const newId = await this.addNote({
          html,
          order: order,
          pageId,
          cardId: 1,
          type,
          pageType
        });

        this.$nextTick(() => {
          //  set render editable
          const ref = this.$refs[newId];
          if (ref) {
            ref.renderEditable = true;
            this.$nextTick(() => {
              console.log(delta);
              if (delta) {
                this.setNoteContent({ noteId: newId, delta: delta });
              }
              this.focusNoteBlock({ noteId: newId });
            });
          } else if (!this.nested && !this.noteId) {
            //  maybe we are on root
            const rootRef = this.$refs['root' + this.pageId];
            if (rootRef) {
              const newNoteDoc = rootRef.$refs.embeddedCard.$refs.cardDoc;
              console.log(newNoteDoc);
              newNoteDoc.$refs[newId].renderEditable = true;
              this.$nextTick(() => {
                if (delta) {
                  this.setNoteContent({ noteId: newId, delta: delta });
                }
                newNoteDoc.focusNoteBlock({ noteId: newId });
              });
            }
          }
        });
      } else {
        this.$emit('addNewLine', { order, delta });
      }
      //  update order of all higher up notes
      //  new vuex note here, returns id

      // const newId = await this.addNoteBlock({
      //   html: '',
      //   order: order,
      //   pageId: this.pageId
      // });

      // this.$nextTick(() => {
      //   if (delta) {
      //     this.setNoteContent({ noteId: newId, delta: delta });
      //   }
      //   this.focusNoteBlock({ noteId: newId });
      // });
    },
    focusNoteBlock({ noteId, index }) {
      if (noteId) {
        this.$refs[noteId].focus();
      } else if (index != undefined) {
        const note = this.documentNotes[index];
        if (note) {
          this.$refs[note.id].focus();
        }
      }
    },
    toggleNoteShow({ noteId, index }) {
      if (noteId) {
        this.$refs[noteId].setShow(true);
      } else if (index != undefined) {
        const note = this.documentNotes[index];
        if (note) {
          this.$refs[note.id].setShow(true);
        }
      }
    },
    setNoteContent({ noteId, delta }) {
      // const contentBlock = this.$refs[noteId][0];
      this.$refs[noteId].setContents(delta);
      // if (!contentBlock.renderEditable) {
      //   console.log('render it');
      //   contentBlock.renderEditable = true;
      //   this.$nextTick(() => {
      //     this.$refs[noteId][0].$refs.editor.setContents(delta);
      //   });
      // } else {
      //   this.$refs[noteId][0].$refs.editor.setContents(delta);
      // }
      // const editor = this.$refs[noteId][0].$refs.editor;
      // if (editor) {
      //   console.log('editor!', editor);
      //   editor.setContents(delta);
      // } else {

      // }
    },
    moveToLine({ toIndex, direction }) {
      const to = this.documentNotes.find(n => n.order == toIndex);
      if (to) {
        const toRef = this.$refs[to.id];
        const index = direction == 'next' ? 0 : toRef.$refs.editor.getLength();
        toRef.$refs.editor.setSelection(index);
      }
    },
    traverseBlocks({ toIndex, direction = 'up', event }) {
      console.log('to index', toIndex);
      try {
        let targetRef = false;
        const prevLeft = event && event.bounds ? event.bounds.left : 0;
        if (toIndex == -1) {
          //  go up to whatever is above this document
          this.$emit('traverseLeaveUp', {
            prevLeft,
            direction
          });
          return;
        } else {
          //  get next element in list
          const target = this.documentNotes[toIndex]; //.find(n => n.order == toIndex);
          targetRef = target ? this.$refs[target.id] : false;
          console.log('targetRef', targetRef);
        }
        if (targetRef) {
          if (toIndex != -1 && targetRef.note.type == 'widget') {
            console.log(targetRef);
            targetRef.$refs.widget.$el.focus();
          } else if (toIndex != -1 && targetRef.note.type == 'card') {
            console.log(targetRef);
            targetRef.$refs.embeddedCard.traverseIn({ event, prevLeft, direction });
          } else {
            //  Put the cursor in the proper position
            //const bound = event && event.bounds ? event.bounds.left : 0;
            if (!targetRef.renderEditable) {
              targetRef.renderEditable = true;
              this.$nextTick(() => {
                targetRef.$refs.editor.setCaret({
                  prevLeft,
                  direction
                });
              });
            } else {
              targetRef.$refs.editor.setCaret({
                prevLeft,
                direction
              });
            }
          }
        } else {
          // go down to whatever is below this document
          console.log(this.$refs.newBtn);
          if (this.$refs.newBtn) {
            this.$refs.newBtn.$el.focus();
          } else {
            this.$emit('traverseLeaveDown', {
              prevLeft,
              direction: 'down'
            });
          }
        }
      } catch (e) {
        console.log(e);
      }
    },
    traverseOutDown() {
      this.$emit('traverseLeaveDown', {
        direction: 'down'
      });
    },
    traverseInto({ event, direction = 'down' }) {
      console.log('traverse direction', direction);
      if (direction == 'up') {
        this.traverseBlocks({ toIndex: this.documentNotes.length - 1, direction, event });
      } else {
        if (this.documentNotes[0]) {
          this.traverseBlocks({ toIndex: 0, direction, event });
        } else {
          //  No doc, focus btn
          this.$refs.newBtn.$el.focus();
          //  no doc notes, create one
          // console.log('create new line');
          // this.addNewLine({ order: 0, type: 'card' });
        }
      }
    }
  }
};
</script>

<style>
.prompt-btn,
.prompt-btn .v-btn__content {
  justify-content: left !important;
}

.drag-selected {
  background: blue;
}

.drag-source .notepad-wrapper {
  pointer-events: none !important;
}

.notepad-wrapper {
  width: 100%;
  position: relative;
}

/* .dl {
  display: flex;
  align-items: stretch;
} */

.reordering-feedback,
.feedback {
  flex: 0 0 100%;
  /* max-width: 100%; */
  /* outline: 1px solid #2196f3; */
  align-self: stretch;
  /* padding-left: 20px; */
  /* border-left: 30px solid rgba(255, 255, 255, 0.8) !important; */
}

.reordering-feedback div,
.feedback div {
  background: lightblue;
  width: 100%;
  height: 5px;
  position: absolute;
  border-radius: 10px;
  z-index: 3;
}

.drag-source .embedded-page {
  background: #eee;
}

.drag-image {
  width: 50px;
  height: 50px;
  transform: translate(-20%, -20%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* .outline .nested .dnd-drag {
  padding-top: 0px;
  padding-bottom: 0px;
}

.outline .nested .type-allowed .dnd-drag {
  padding-top: 2px;
  padding-bottom: 2px;
}

.outline .dnd-drag {
  transition: padding 0.1s !important;
} */
</style>
